import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const WINDOWS = [
  ["morning", "Morning (8am-12pm)", 8],
  ["afternoon", "Afternoon (12pm-5pm)", 12],
  ["evening", "Evening (5pm-9pm)", 17],
];
const START = { morning: 8, afternoon: 12, evening: 17 };
const iso = (d) => d.toISOString().slice(0, 10);

// Collection slot start as a UTC datetime (matches backend slot_start_dt).
function slotStartUTC(dateISO, window) {
  const d = new Date(dateISO + "T00:00:00Z");
  d.setUTCHours(START[window], 0, 0, 0);
  return d.getTime();
}

export default function SlotPicker({ open, onClose, onSelect, mode = "collection", title, collectionSlot, freeItem, courierString, leaveConfig, alreadyLeaveAtDoor }) {
  // Build the valid (date, window) combinations for this picker.
  const { dateList, validKeys } = useMemo(() => {
    if ((mode === "delivery" || mode === "return_delivery") && collectionSlot?.date && collectionSlot?.window) {
      const start = slotStartUTC(collectionSlot.date, collectionSlot.window);
      const min = start + 24 * 3600 * 1000;
      const max = start + (mode === "delivery" ? 48 : 96) * 3600 * 1000;
      const keys = new Set();
      const dates = [];
      for (let off = 0; off <= 4; off++) {
        const base = new Date(collectionSlot.date + "T00:00:00Z");
        base.setUTCDate(base.getUTCDate() + off);
        const dISO = iso(base);
        let any = false;
        for (const [w] of WINDOWS) {
          const t = slotStartUTC(dISO, w);
          if (t >= min && t <= max) { keys.add(`${dISO}|${w}`); any = true; }
        }
        if (any && !dates.includes(dISO)) dates.push(dISO);
      }
      return { dateList: dates, validKeys: keys };
    }
    // collection — must start >=24h from now; return — next 3 days from today
    const now = new Date();
    const minTs = ["collection", "delivery_rebook", "dr_receipt"].includes(mode) ? now.getTime() + 24 * 3600 * 1000 : 0;
    const keys = new Set();
    const dates = [];
    for (let off = 0; off <= 5 && dates.length < 3; off++) {
      const d = new Date(now); d.setDate(d.getDate() + off);
      const dISO = iso(d);
      let any = false;
      for (const [w] of WINDOWS) {
        if (slotStartUTC(dISO, w) >= minTs) { keys.add(`${dISO}|${w}`); any = true; }
      }
      if (any) dates.push(dISO);
    }
    return { dateList: dates, validKeys: keys };
  }, [mode, collectionSlot]);

  const [date, setDate] = useState(dateList[0] || "");
  const [window, setWindow] = useState("");
  const [leaveAtDoor, setLeaveAtDoor] = useState(!!leaveConfig?.defaultOn);
  const [ack, setAck] = useState(false);
  const fmtDate = (s) => new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const activeDate = date || dateList[0] || "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="slot-picker">
        <DialogHeader>
          <DialogTitle>{title || "Select a time slot"}</DialogTitle>
          <DialogDescription>
            {mode === "delivery" ? "Choose a delivery window between 24 and 48 hours after the seller's collection slot." : mode === "return_delivery" ? "Choose a return receipt window at least 24 hours after the buyer's return collection slot." : ["collection", "delivery_rebook", "dr_receipt"].includes(mode) ? "Choose a window at least 24 hours from now." : "Choose a date and time window for the courier."}
          </DialogDescription>
        </DialogHeader>
        {mode === "delivery" && courierString && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="delivery-courier-string">
            <span className="font-semibold text-slate-700">Deliver to:</span> {courierString}
          </div>
        )}
        {!freeItem && alreadyLeaveAtDoor && mode === "delivery_rebook" && (
          <div data-testid="rebook-already-lad" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            You chose leave at door. Your item will be left at your door and photographed by the courier.
          </div>
        )}
        {freeItem && ["delivery", "delivery_rebook"].includes(mode) && (
          <div data-testid="free-leave-at-door" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Your item will be left at your door by the courier. A photo will be taken upon delivery as confirmation.
          </div>
        )}
        {!freeItem && (mode === "delivery" || (mode === "delivery_rebook" && !alreadyLeaveAtDoor)) && (
          <div data-testid="leave-at-door-toggle" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
              <input type="checkbox" checked={leaveAtDoor} onChange={(e) => setLeaveAtDoor(e.target.checked)} data-testid="leave-at-door-checkbox" />
              Leave at my door
            </label>
            <div className="mt-1 space-y-1 text-[11px] leading-snug text-slate-400" data-testid="leave-at-door-terms">
              <p>By choosing leave at door, you accept responsibility for your item once the courier has photographed it at your door. SellStuff is not responsible for loss, theft or damage after that point. You can still raise a concern within 24 hours if the wrong item was delivered, it arrived damaged, it is significantly not as described, or items shown in the listing are missing.</p>
            </div>
          </div>
        )}
        {leaveConfig?.show && (
          <div data-testid="return-leave-at-door" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
              <input type="checkbox" checked={leaveAtDoor} onChange={(e) => setLeaveAtDoor(e.target.checked)} data-testid="return-leave-at-door-checkbox" />
              {leaveConfig.label || "Leave at my door"}
            </label>
            <p className="mt-1 text-[11px] leading-snug text-slate-400" data-testid="return-leave-at-door-terms">{leaveConfig.terms}</p>
          </div>
        )}
        {leaveConfig?.rules && (
          <div data-testid="return-rules" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
            <p className="text-xs font-semibold text-slate-700">Return collection</p>
            <ul className="mt-1 space-y-0.5 text-[11px] leading-snug text-slate-500">
              {leaveConfig.rules.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          </div>
        )}
        {leaveConfig?.requireAck && (
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-800">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="return-ack-checkbox" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{leaveConfig.ackLabel}</span>
          </label>
        )}
        <p className="text-sm text-slate-500">
          {mode === "delivery" ? "Available between 24 and 48 hours after the seller's collection slot." : mode === "return_delivery" ? "Available from 24 hours after the buyer's return collection slot." : ["collection", "delivery_rebook", "dr_receipt"].includes(mode) ? "Choose a slot at least 24 hours from now." : "Choose any of the next 3 days."}
        </p>
        {dateList.length === 0 ? (
          <p className="py-4 text-sm text-slate-500" data-testid="slot-none">No slots available yet.</p>
        ) : (
          <>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Date</p>
              <div className="flex flex-wrap gap-2">
                {dateList.map((v) => (
                  <button key={v} data-testid={`slot-date-${v}`} onClick={() => { setDate(v); setWindow(""); }} className={`rounded-full border px-3 py-1.5 text-sm ${activeDate === v ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-700"}`}>{fmtDate(v)}</button>
                ))}
              </div>
            </div>
            <div className="mt-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Time window</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {WINDOWS.map(([w, l]) => {
                  const ok = validKeys.has(`${activeDate}|${w}`);
                  return <button key={w} disabled={!ok} data-testid={`slot-window-${w}`} onClick={() => ok && setWindow(w)} className={`rounded-xl border px-3 py-2 text-sm ${window === w ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-700"} ${!ok ? "cursor-not-allowed opacity-40" : ""}`}>{l}</button>;
                })}
              </div>
            </div>
            <Button data-testid="slot-confirm" disabled={!window || (leaveConfig?.requireAck && !ack)} onClick={() => onSelect({ date: activeDate, window, leave_at_door: freeItem || alreadyLeaveAtDoor ? true : leaveAtDoor, leave_ack: leaveConfig?.requireAck ? ack : true })} className="mt-4 w-full rounded-full bg-primary font-semibold">Confirm slot</Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
